import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonChip,
  IonCol,
  IonIcon,
  IonLabel,
  IonLoading,
  IonRange,
  IonRow,
} from '@ionic/react';
import { checkmarkCircle, closeCircle, heartOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Content from '../components/Content';
import PleaseLoginCard from '../components/PleaseLoginCard';
import { useProfile } from '../data/store/useProfile';
import { confirmGroupCheckout, redirectToGroupCheckout } from '../services/StripeService';
import { usePageTitle } from '../util/sugar';
import './CourseraJoin.scss';

// Songwriting Craft & Practice (Coursera) — see the invite link this mirrors:
// /invitation/?session-id=83441683-a16b-40fd-afd0-8851930becb4&title=...
// This page is a paid alternative to that manual password-invite flow: pay,
// and you're added to the same group automatically. One-off, hardcoded on
// purpose rather than made generic — there's only one group this page is for.
const GROUP_ID = '83441683-a16b-40fd-afd0-8851930becb4';
const GROUP_TITLE = 'Songwriting Craft & Practice (Coursera)';
const MIN_AMOUNT = 5;
const MAX_AMOUNT = 10;

const CourseraJoin: React.FC = () => {
  usePageTitle(`Join ${GROUP_TITLE}`);
  const location = useLocation();
  const history = useHistory();
  const { authenticated, authorId } = useProfile();
  const authStatus = useProfile((x) => x.status);

  const [amount, setAmount] = useState<number>(MIN_AMOUNT);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      const sessionId = params.get('session_id');
      history.replace('/coursera');

      if (!sessionId) {
        setErrorMessage('Payment succeeded, but we lost track of the session. Please contact support to confirm your access.');
        return;
      }

      setIsConfirming(true);
      confirmGroupCheckout(sessionId)
        .then(() => {
          setSuccessMessage("You're in! Head to the group to start submitting songs.");
        })
        .catch((error: any) => {
          console.error('Coursera confirm error:', error);
          setErrorMessage(error.message || 'Payment succeeded, but we could not confirm it automatically. Please contact support.');
        })
        .finally(() => setIsConfirming(false));
    } else if (params.get('canceled') === 'true') {
      setErrorMessage("Payment canceled — you haven't been charged. Feel free to try again when you're ready.");
      history.replace('/coursera');
    }
  }, [location.search, history]);

  // Wait for auth to resolve before deciding what to show — otherwise the
  // brief "unknown"/"authenticating" startup window flashes the login card
  // at people who are actually already signed in (same guard as Invitation.tsx).
  if (authStatus === 'unknown' || authStatus === 'authenticating') {
    return (
      <Content>
        <IonLoading mode="md" isOpen={true} message="Checking sign-in..." />
      </Content>
    );
  }

  if (!authenticated) {
    return (
      <Content>
        <PleaseLoginCard portal={window.location.href} action="Join & Pay" />
      </Content>
    );
  }

  const handleJoin = async () => {
    setIsProcessing(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await redirectToGroupCheckout({
        groupId: GROUP_ID,
        artistId: authorId!,
        amount,
        productName: `${GROUP_TITLE} — Song Share Access`,
        productDescription: 'One-time contribution to join the Coursera group and submit songs.',
      });
    } catch (error: any) {
      console.error('Coursera join error:', error);
      setErrorMessage(error.message || 'Failed to start payment. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <Content>
      <IonLoading mode="md" isOpen={isConfirming} message="Confirming your payment..." />
      <IonRow>
        <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
          <IonCard className="coursera-join-card">
            <IonCardHeader>
              <h1 className="coursera-join-title">{GROUP_TITLE}</h1>
              <p className="coursera-join-subtitle">
                Join the group to submit your songs. Pick a contribution amount that works for
                you — anywhere from ${MIN_AMOUNT} to ${MAX_AMOUNT} — and you'll be added
                automatically as soon as payment goes through.
              </p>
            </IonCardHeader>

            <IonCardContent>
              {successMessage && (
                <div className="message-section" role="status" aria-live="polite">
                  <IonChip color="success" className="message-chip">
                    <IonIcon icon={checkmarkCircle} aria-hidden="true" />
                    <IonLabel>{successMessage}</IonLabel>
                  </IonChip>
                  <IonButton expand="block" routerLink={`/session-songs/${GROUP_ID}`}>
                    Go to the group
                  </IonButton>
                </div>
              )}

              {errorMessage && (
                <div className="message-section" role="alert" aria-live="assertive">
                  <IonChip color="danger" className="message-chip">
                    <IonIcon icon={closeCircle} aria-hidden="true" />
                    <IonLabel>{errorMessage}</IonLabel>
                  </IonChip>
                </div>
              )}

              {!successMessage && (
                <>
                  <div className="section">
                    <h3 className="section-title" id="coursera-amount-label">
                      Choose your contribution: ${amount}
                    </h3>
                    <IonRange
                      aria-labelledby="coursera-amount-label"
                      min={MIN_AMOUNT}
                      max={MAX_AMOUNT}
                      step={1}
                      snaps={true}
                      pin={true}
                      value={amount}
                      onIonChange={(e) => setAmount(e.detail.value as number)}
                    />
                  </div>

                  <div className="section">
                    <IonButton
                      expand="block"
                      size="large"
                      onClick={handleJoin}
                      disabled={isProcessing}
                      className="coursera-join-button"
                    >
                      <IonIcon icon={heartOutline} slot="start" aria-hidden="true" />
                      {isProcessing ? 'Redirecting to payment...' : `Continue to Payment - $${amount}`}
                    </IonButton>
                  </div>
                </>
              )}
            </IonCardContent>
          </IonCard>
        </IonCol>
      </IonRow>
    </Content>
  );
};

export default CourseraJoin;
